import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { RotateCcw, SkipBack, SkipForward, Play, X, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

export function SimulatorBar({
  currentAutomaton, simInput, setSimInput,
  simResult, simStep, simFinished, simCurrentStates, simAutoRun, simAnimating,
  simSpeed, onSpeedChange,
  onStepBack, onStep, onRun, onReset, onClose,
}) {
  const isTM = currentAutomaton?.type === 'TM';

  return (
    <div className="border-b border-border flex items-center gap-3 px-4 py-2 bg-card shrink-0" data-testid="simulator-bar">
      {/* Left: Input */}
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs font-medium text-muted-foreground">Test:</span>
        {simStep === -1 ? (
          <textarea
            rows={1}
            value={simInput}
            onChange={(e) => {
              let v = e.target.value.replace(/\n/g, '');
              if (isTM) v = v.replace(/\\_/g, '\u25A1').replace(/(?<![\\])_/g, '\u25A1');
              setSimInput(v);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onStep(); } }}
            placeholder={isTM ? 'tape input' : 'input string'}
            className="w-36 h-7 px-2 py-1 text-sm font-mono bg-muted/50 border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-ring resize-none overflow-hidden leading-tight"
            autoFocus
            data-testid="sim-input"
          />
        ) : (
          <span className="text-sm font-mono text-muted-foreground px-2 py-0.5 bg-muted/40 rounded">{simInput || '\u03B5'}</span>
        )}
      </div>

      {/* Center: Character progress + status */}
      <div className="flex-1 flex items-center justify-center gap-3">
        {simStep >= 0 && simResult && (
          <>
            {isTM ? (
              /* TM: show step counter instead of character display */
              <div className="flex items-center gap-2 font-mono text-sm" data-testid="sim-tm-step-display">
                <span className="text-xs text-muted-foreground">
                  Step {simStep} / {simResult.path ? simResult.path.length - 1 : '?'}
                </span>
                <span className="text-xs text-muted-foreground font-mono px-1.5 py-0.5 bg-muted/60 rounded">
                  {simCurrentStates.map(id =>
                    currentAutomaton.states.find(s => s.id === id)?.name
                  ).filter(Boolean).join(', ') || '\u2205'}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-0.5 font-mono text-sm" data-testid="sim-char-display">
                {simInput.length === 0 ? (
                  <span className="text-muted-foreground italic text-xs px-1">{'\u03B5'}</span>
                ) : (
                  simInput.split('').map((char, i) => (
                    <span
                      key={i}
                      className={`w-6 h-6 flex items-center justify-center rounded text-sm transition-colors ${
                        i < simStep
                          ? 'text-muted-foreground/40'
                          : i === simStep && !simFinished
                          ? 'bg-primary/15 text-primary font-semibold ring-1 ring-primary/25'
                          : 'text-foreground'
                      }`}
                    >
                      {char}
                    </span>
                  ))
                )}
                <span className="text-xs text-muted-foreground ml-2 font-mono px-1.5 py-0.5 bg-muted/60 rounded">
                  {simCurrentStates.map(id =>
                    currentAutomaton.states.find(s => s.id === id)?.name
                  ).filter(Boolean).join(', ') || '\u2205'}
                </span>
              </div>
            )}
            <div
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full shrink-0 ${
                simFinished && simResult.status === 'accepted' ? 'bg-green-500/15 text-green-600 dark:text-green-400' :
                simFinished && (simResult.status === 'rejected' || simResult.status === 'stuck') ? 'bg-red-500/15 text-red-600 dark:text-red-400' :
                simFinished && simResult.status === 'loop' ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' :
                simResult.status === 'no-initial' ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' :
                'bg-blue-500/10 text-blue-600 dark:text-blue-400'
              }`}
              data-testid="sim-status"
            >
              {simFinished ? (
                simResult.status === 'accepted' ? <><CheckCircle2 className="w-3.5 h-3.5" /> Accepted</> :
                simResult.status === 'stuck' ? <><XCircle className="w-3.5 h-3.5" /> Stuck</> :
                simResult.status === 'loop' ? <><AlertTriangle className="w-3.5 h-3.5" /> Step limit</> :
                simResult.status === 'no-initial' ? <><AlertTriangle className="w-3.5 h-3.5" /> No initial state</> :
                <><XCircle className="w-3.5 h-3.5" /> Rejected</>
              ) : (
                isTM
                  ? <>Step {simStep}</>
                  : <>{simStep}/{simInput.length}</>
              )}
            </div>
          </>
        )}
      </div>

      {/* Right: Controls — Reset | Step + Speed | Run */}
      <div className="flex items-center gap-1 shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onReset} disabled={simStep < 0} data-testid="sim-reset-btn">
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Reset</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onStepBack} disabled={!simResult || simStep <= 0 || simAnimating} data-testid="sim-step-back-btn">
              <SkipBack className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Step back</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onStep} disabled={simFinished || simAnimating} data-testid="sim-step-btn">
              <SkipForward className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Step forward</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onSpeedChange(simSpeed === 'slow' ? 'fast' : 'slow')}
              className="h-7 px-2 text-xs font-mono font-medium rounded transition-colors hover:bg-muted text-muted-foreground"
              data-testid="sim-speed-btn"
            >
              {simSpeed === 'fast' ? '2x' : '1x'}
            </button>
          </TooltipTrigger>
          <TooltipContent>Animation speed</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onRun} disabled={simFinished || simAutoRun || simAnimating} data-testid="sim-run-btn">
              <Play className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Run to completion</TooltipContent>
        </Tooltip>
        <button onClick={onClose} className="p-1 hover:bg-muted rounded transition-colors" data-testid="sim-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
